/**
 * CCDateTimePicker
 *
 * Date + TIME field, styled to match the OTB `CCDatePicker` (same border radius /
 * colour / font size, same compact single-row footprint) — the only functional
 * difference is that this one also captures a TIME.
 *
 * Two input methods, both required by the host UX:
 *   1. TYPE — the field is a real text-editable `datetime-local` input, so the
 *      host can just type/paste the date and time.
 *   2. CLICK — the calendar button (and clicking the field itself) calls the
 *      input's native `showPicker()`, which pops the browser's own calendar +
 *      time selector. We surface an explicit button because the browser's own
 *      built-in indicator is a tiny glyph that is easy to miss.
 *
 * Why native rather than a JS calendar: `react-native-ui-datepicker@2.0.12`
 * CRASHES on react-native-web as soon as a time picker is involved — its wheel
 * animation runs through `AnimatedMath.sin` → `Animated.modulo`, and RN-Web does
 * not implement `Animated.modulo`. (CCLeague only uses that library in
 * `mode="range"` with no time picker, which is why it works there.) The native
 * control has no such problem and matches OTB's date field visually.
 *
 * The value is a LOCAL Date (what the host sees/picks); the caller converts to a
 * UTC ISO string on the wire.
 */

import React, {useRef} from 'react';
import {Pressable} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import Box from '@/Atoms/Box';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';

const TEAL_DARK = '#1F6C63';

interface Props {
  /** Current value (local Date). */
  value: Date;
  /** Fires with a new local Date whenever the user changes date or time. */
  onChange: (d: Date) => void;
  /** Earliest selectable moment (caller typically passes `new Date()`). */
  minDate?: Date;
  /** Timezone label shown beside the field (e.g. "IST"), display-only. */
  tzLabel?: string;
}

/** Format a Date as the `YYYY-MM-DDTHH:mm` string `datetime-local` expects
 *  (LOCAL time — deliberately not toISOString, which would shift to UTC). */
function toLocalInputValue(d: Date): string {
  if (!(d instanceof Date) || isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export default function CCDateTimePicker({
  value,
  onChange,
  minDate,
  tzLabel,
}: Props) {
  const inputRef = useRef<any>(null);
  const raw = toLocalInputValue(value);

  /** Open the browser's native calendar/time popup. `showPicker()` is supported
   *  in current Chrome/Edge/Safari; where it isn't, the field is still fully
   *  typable and the browser's own indicator remains clickable, so this is a
   *  progressive enhancement rather than the only way in. */
  const openNativePicker = () => {
    const el = inputRef.current;
    if (!el) {
      return;
    }
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch (e) {
      // Some browsers throw if showPicker() isn't user-activated — focusing the
      // field still lets the host type, so fail soft.
      el.focus();
    }
  };

  return (
    <Box
      style={{
        borderRadius: 10,
        borderColor: CCColors.Grey.Border,
        borderWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
      }}>
      {/* @ts-ignore RN-web forwards DOM props to the host input. */}
      <input
        ref={inputRef}
        type="datetime-local"
        value={raw}
        min={minDate ? toLocalInputValue(minDate) : undefined}
        // Clicking anywhere in the field opens the native picker too, while
        // still leaving the text editable for direct typing.
        onClick={openNativePicker}
        onChange={(e: any) => {
          const v = e.target.value;
          if (!v) {
            return;
          }
          // `YYYY-MM-DDTHH:mm` parses as LOCAL time, which is what we want.
          const ms = Date.parse(v);
          if (!isNaN(ms)) {
            onChange(new Date(ms));
          }
        }}
        style={
          {
            flex: 1,
            margin: 10,
            fontSize: FontSizes[14],
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: raw ? 'inherit' : '#A8A7A6',
            fontFamily: 'inherit',
          } as any
        }
      />

      {tzLabel ? (
        <span
          style={
            {
              marginRight: 8,
              fontSize: FontSizes[12],
              color: CCColors.TextColor.GreyText,
            } as any
          }>
          {tzLabel}
        </span>
      ) : null}

      {/* Explicit affordance for the "click to pick" path. */}
      <Pressable
        onPress={openNativePicker}
        style={{paddingHorizontal: 10, paddingVertical: 10}}>
        <MaterialCommunityIcons
          name="calendar-clock"
          size={18}
          color={TEAL_DARK}
        />
      </Pressable>
    </Box>
  );
}
